import type { App } from '@keboola/api-client/developerPortal/types';

import { getComponentTypeLabel, getStageBadge, isFirstParty } from '../helpers';

import { ComponentBadges } from './ComponentBadges';

const UI_OPTION_FULL_ACCESS = 'appInfo.fullAccess';
const UI_OPTION_DATA_IN = 'appInfo.dataIn';
const UI_OPTION_DATA_OUT = 'appInfo.dataOut';

const ExternalLink = ({ href, label }: { href: string; label: string }) => (
  <li className="tw:truncate">
    {label}:{' '}
    <a href={href} target="_blank" rel="noopener noreferrer" className="tw:text-primary">
      {href}
    </a>
  </li>
);

/**
 * The public facts about a component, in the original's exact order — stage, ID, type, categories,
 * third-party status, support, access/fee/data flags, then the three links.
 *
 * Order is behaviour here, not styling: users scan this list, and the original put support attribution
 * immediately after the third-party marker for a reason. Every `uiOptions` / `categories` read is
 * guarded, because the original called `.includes()` and `.length` unguarded and would throw on a
 * record missing either.
 */
export function PublicFlags({ app }: { app: App }) {
  const uiOptions = app.uiOptions ?? [];
  const categories = app.categories ?? [];
  const thirdParty = !isFirstParty(app);

  return (
    <ul className="tw:m-0 tw:flex tw:list-none tw:flex-col tw:gap-1.5 tw:p-0 tw:text-sm tw:text-muted-foreground">
      {getStageBadge(app) !== null && (
        <li className="tw:flex tw:items-center tw:gap-2">
          Stage
          <ComponentBadges app={app} />
        </li>
      )}

      <li>ID — {app.id}</li>

      {Boolean(app.type) && <li>Component type — {getComponentTypeLabel(app.type)}</li>}

      {categories.length > 0 && <li>Categories — {categories.join(', ')}</li>}

      {thirdParty && <li>3rd party component</li>}
      <li>
        {thirdParty
          ? 'Support is provided by its author, not Keboola'
          : 'Support is provided by Keboola'}
      </li>

      {uiOptions.includes(UI_OPTION_FULL_ACCESS) && (
        <li>Will have full access to the project including all its data.</li>
      )}
      {app.fees === true && <li>Extra charge to use this component</li>}
      {uiOptions.includes(UI_OPTION_DATA_IN) && <li>Extracts data from outside sources</li>}
      {uiOptions.includes(UI_OPTION_DATA_OUT) && <li>Sends data outside of Keboola</li>}

      {typeof app.licenseUrl === 'string' && app.licenseUrl !== '' && (
        <ExternalLink href={app.licenseUrl} label="License" />
      )}
      {typeof app.documentationUrl === 'string' && app.documentationUrl !== '' && (
        <ExternalLink href={app.documentationUrl} label="Documentation" />
      )}
      {typeof app.sourceCodeUrl === 'string' && app.sourceCodeUrl !== '' && (
        <ExternalLink href={app.sourceCodeUrl} label="Source code" />
      )}
    </ul>
  );
}
